import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Time-window options for temperature & battery graphs.
// Values must match GRAPH_RANGES keys in router.cjs.
const GRAPH_WINDOWS = [
  { value: '1h',  label: '1 Hour'   },
  { value: '1d',  label: '1 Day'    },
  { value: '7d',  label: '7 Days'   },
  { value: '14d', label: '14 Days'  },
  { value: '30d', label: '30 Days'  },
];

// For ranges shorter than a day we only show the time; for multi-day ranges we
// include the date so adjacent buckets are distinguishable on the X-axis.
const getTimeLabel = (date, range) => {
  const isSubDay = range === '1h' || range === '1d';
  if (isSubDay) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })}`;
};

export default function DeviceGraphModal({ isOpen, onClose, androidId, deviceName, graphType }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('1d');
  // Temperature series visibility toggles (only used when graphType === 'temperature')
  const [showBatteryTemp, setShowBatteryTemp] = useState(true);
  const [showDeviceTemp, setShowDeviceTemp]   = useState(true);
  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  useEffect(() => {
    if (isOpen && androidId) {
      fetchData();
    }
  }, [isOpen, androidId, graphType, range]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Temperature & battery graphs: backend returns ordered, server-bucketed rows for the
      // selected window, so we just map them and skip the speed formatting below.
      if (graphType === 'temperature' || graphType === 'battery') {
        const response = await axios.get(
          `${API_BASE}/maiApi/device/${androidId}/${graphType}?range=${range}`
        );
        const formatted = response.data
          .map(item => {
            const date = new Date(item.timestamp);
            return graphType === 'temperature'
              ? {
                  time:         getTimeLabel(date, range),
                  timestamp:    item.timestamp,
                  dateTime:     date,
                  battery_temp: item.battery_temp  != null ? parseFloat(item.battery_temp)  : null,
                  device_temp:  item.device_temp   != null ? parseFloat(item.device_temp)   : null,
                }
              : {
                  time:      getTimeLabel(date, range),
                  timestamp: item.timestamp,
                  dateTime:  date,
                  battery:   item.battery_percent != null ? parseFloat(item.battery_percent) : null,
                };
          })
          .sort((a, b) => a.dateTime - b.dateTime);
        setData(formatted);
        return;
      }

      // ── Speed graph (unchanged) ──────────────────────────────────────────────
      const endpoint = `${API_BASE}/maiApi/device/${androidId}/speed-24h`;
      const response = await axios.get(endpoint);

      let formattedData = response.data
        .map(item => {
          const date = new Date(item.timestamp);
          let timeLabel;
          if (graphType === 'speed') {
            const hour = date.getHours();
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            timeLabel = `${displayHour} ${ampm}`;
          } else {
            const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
            const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            timeLabel = `${dateStr} ${timeStr}`;
          }
          return {
            time:      timeLabel,
            timestamp: item.timestamp,
            dateTime:  date,
            hour:      date.getHours(),
            hourUTC:   date.getUTCHours(),
            ...(graphType === 'speed' ? {
              download: item.download_mbps != null ? parseFloat(item.download_mbps) : null,
              upload:   item.upload_mbps   != null ? parseFloat(item.upload_mbps)   : null,
              ping:     item.ping_ms       != null ? parseFloat(item.ping_ms)       : null,
            } : {
              battery: item.battery_percent != null ? parseFloat(item.battery_percent) : null,
            })
          };
        })
        .sort((a, b) => a.dateTime - b.dateTime);

      if (graphType === 'speed') {
        const now = new Date();
        const currentHour = new Date(now);
        currentHour.setMinutes(0);
        currentHour.setSeconds(0);
        currentHour.setMilliseconds(0);
        const startTime = new Date(currentHour.getTime() - 24 * 60 * 60 * 1000);
        const hourlySlots = [];
        for (let i = 0; i < 24; i++) {
          const slotTime = new Date(startTime.getTime() + i * 60 * 60 * 1000);
          const hour = slotTime.getHours();
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          const timeLabel = `${displayHour} ${ampm}`;
          const matchingData = formattedData.find(item => {
            const itemTime = new Date(item.dateTime);
            itemTime.setMinutes(0);
            itemTime.setSeconds(0);
            itemTime.setMilliseconds(0);
            const slotNorm = new Date(slotTime);
            slotNorm.setMinutes(0);
            slotNorm.setSeconds(0);
            slotNorm.setMilliseconds(0);
            return Math.abs(itemTime.getTime() - slotNorm.getTime()) < 60 * 60 * 1000;
          });
          if (matchingData) {
            hourlySlots.push({ ...matchingData, time: timeLabel, hour });
          } else {
            hourlySlots.push({ time: timeLabel, timestamp: slotTime.toISOString(), dateTime: slotTime, hour, download: null, upload: null, ping: null });
          }
        }
        formattedData = hourlySlots;
      }

      setData(formattedData);
    } catch (err) {
      console.error('Error fetching graph data:', err);
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedWindowLabel = GRAPH_WINDOWS.find(w => w.value === range)?.label ?? range;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-blue-600 text-white p-4 flex items-center justify-between rounded-t-lg">
          <div>
            <h2 className="text-xl font-semibold">
              {graphType === 'speed'
                ? 'Speed History (24 Hours)'
                : graphType === 'temperature'
                ? `Temperature History (${selectedWindowLabel})`
                : `Battery History (${selectedWindowLabel})`}
            </h2>
            <p className="text-sm text-blue-100">{deviceName || androidId}</p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <FaTimes size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Controls row: time-range dropdown + optional temperature toggles */}
          {(graphType === 'temperature' || graphType === 'battery') && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              {/* Temperature series toggles */}
              {graphType === 'temperature' && (
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={showBatteryTemp}
                      onChange={(e) => setShowBatteryTemp(e.target.checked)}
                      className="w-4 h-4 accent-amber-500 cursor-pointer"
                    />
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-0.5 bg-amber-500 rounded" />
                      Battery Temp
                    </span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={showDeviceTemp}
                      onChange={(e) => setShowDeviceTemp(e.target.checked)}
                      className="w-4 h-4 accent-red-500 cursor-pointer"
                    />
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-3 h-0.5 bg-red-500 rounded" />
                      Device Temp
                    </span>
                  </label>
                </div>
              )}
              {/* Spacer when battery graph (no toggles) */}
              {graphType === 'battery' && <span />}

              {/* Time-range dropdown */}
              <div className="flex items-center gap-2">
                <label htmlFor="graph-range-select" className="text-sm font-medium text-gray-600 whitespace-nowrap">
                  Time range:
                </label>
                <select
                  id="graph-range-select"
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {GRAPH_WINDOWS.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <FaSpinner className="animate-spin text-4xl text-blue-600" />
            </div>
          ) : error ? (
            <div className="text-center text-red-600 p-8">
              <p>Error: {error}</p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center text-gray-500 p-8">
              <p>
                {graphType === 'temperature' || graphType === 'battery'
                  ? `No ${graphType} data available for the selected window (${selectedWindowLabel})`
                  : 'No data available for the last 24 hours'}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                  interval={graphType === 'speed' ? 0 : 'preserveStartEnd'}
                  tickFormatter={(value, index) => {
                    if (graphType === 'speed') return value;
                    const totalTicks = data.length;
                    const showEvery = Math.max(1, Math.floor(totalTicks / 12));
                    return (index % showEvery === 0 || index === totalTicks - 1) ? value : '';
                  }}
                />
                <YAxis
                  domain={graphType === 'battery' ? [0, 100] : ['auto', 'auto']}
                  label={{
                    value: graphType === 'speed' ? 'Speed (Mbps)' : graphType === 'temperature' ? 'Temperature (°F)' : 'Battery (%)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' },
                  }}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (value == null) return ['N/A', name];
                    return [typeof value === 'number' ? value.toFixed(2) : value, name];
                  }}
                  labelFormatter={(label) => {
                    const dataPoint = data.find(d => d.time === label);
                    if (dataPoint?.timestamp) {
                      return new Date(dataPoint.timestamp).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', hour12: true,
                      });
                    }
                    return label;
                  }}
                />
                <Legend verticalAlign="top" height={36} />

                {graphType === 'speed' ? (
                  <>
                    <Line type="monotone" dataKey="download" stroke="#8884d8" strokeWidth={2} name="Download (Mbps)" dot={{ r: 2 }} connectNulls={false} />
                    <Line type="monotone" dataKey="upload"   stroke="#82ca9d" strokeWidth={2} name="Upload (Mbps)"   dot={{ r: 2 }} connectNulls={false} />
                  </>
                ) : graphType === 'temperature' ? (
                  <>
                    {showBatteryTemp && (
                      <Line type="monotone" dataKey="battery_temp" stroke="#f59e0b" strokeWidth={2} name="Battery (°F)" dot={{ r: 2 }} connectNulls={false} />
                    )}
                    {showDeviceTemp && (
                      <Line type="monotone" dataKey="device_temp"  stroke="#ef4444" strokeWidth={2} name="Device (°F)"  dot={{ r: 2 }} connectNulls={false} />
                    )}
                  </>
                ) : (
                  <Line type="monotone" dataKey="battery" stroke="#ffc658" strokeWidth={2} name="Battery (%)" dot={{ r: 2 }} connectNulls={false} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
